import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { CloudUpload, Description, Close } from "@mui/icons-material";

const ALLOWED_TYPES = ["application/pdf", "image/png"];
const MAX_SIZE = 10 * 1024 * 1024;

interface WithdrawalProofDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (file: File) => Promise<void>;
}

const WithdrawalProofDialog = ({
  open,
  onClose,
  onSubmit,
}: WithdrawalProofDialogProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    e.target.value = "";
    if (!selected) return;

    if (!ALLOWED_TYPES.includes(selected.type)) {
      setError("Proof must be a PDF or PNG file.");
      return;
    }
    if (selected.size > MAX_SIZE) {
      setError("File is too large (max 10MB).");
      return;
    }
    setError(null);
    setFile(selected);
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!file) return;
    setSubmitting(true);
    try {
      await onSubmit(file);
      handleClose();
    } catch {
      setError("Failed to approve withdrawal. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        Attach Proof of Payment
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Approving this withdrawal requires a proof of payment (PDF or PNG).
        </Typography>

        <input
          type="file"
          accept="application/pdf,image/png"
          id="withdrawal-proof-upload"
          hidden
          onChange={handleFileChange}
        />
        <label htmlFor="withdrawal-proof-upload">
          <Box
            sx={{
              border: "2px dashed",
              borderColor: file ? "#0D9488" : "divider",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              "&:hover": { borderColor: "#0D9488" },
            }}
          >
            {file ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Description sx={{ color: "#0D9488" }} />
                <Typography variant="body2">{file.name}</Typography>
                <Close
                  fontSize="small"
                  sx={{ cursor: "pointer", color: "text.secondary" }}
                  onClick={(e) => {
                    e.preventDefault();
                    setFile(null);
                  }}
                />
              </Box>
            ) : (
              <>
                <CloudUpload sx={{ fontSize: 32, color: "#bdbdbd" }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Click to select a PDF or PNG file
                </Typography>
              </>
            )}
          </Box>
        </label>

        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !file}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            bgcolor: "#0D9488",
            "&:hover": { bgcolor: "#2d8c84" },
          }}
        >
          {submitting ? "Approving..." : "Approve Withdrawal"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WithdrawalProofDialog;
